import React, { useState, useEffect } from "react";
import { GALLERY, HISTORICAL } from "../../mock";
import { X, Camera, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { SectionTitle } from "./About";
import { listPhotos } from "../../lib/api";

// Build a richer gallery: recent + 10 historical + any uploaded by admin
const FEATURED = [
  { src: GALLERY[3].src, caption: "At the library of awards", category: "Office", featured: true },
  { src: GALLERY[1].src, caption: "Consulting at Cosmopolitan Hospitals", category: "Practice" },
  { src: HISTORICAL[1].src, caption: HISTORICAL[1].caption, category: "Honors", enhance: true },
  { src: HISTORICAL[6].src, caption: HISTORICAL[6].caption, category: "Honors", enhance: true },
  { src: GALLERY[2].src, caption: "At the writing desk", category: "Author" },
  { src: HISTORICAL[3].src, caption: HISTORICAL[3].caption, category: "Royal", enhance: true },
  { src: HISTORICAL[7].src, caption: HISTORICAL[7].caption, category: "Public Life", enhance: true },
  { src: GALLERY[0].src, caption: "In traditional Kerala attire", category: "Personal" },
  { src: HISTORICAL[2].src, caption: HISTORICAL[2].caption, category: "Honors", enhance: true },
  { src: HISTORICAL[8].src, caption: HISTORICAL[8].caption, category: "Honors", enhance: true },
  { src: HISTORICAL[4].src, caption: HISTORICAL[4].caption, category: "Public Life", enhance: true },
  { src: HISTORICAL[5].src, caption: HISTORICAL[5].caption, category: "Honors", enhance: true },
  { src: HISTORICAL[10].src, caption: HISTORICAL[10].caption, category: "Public Life", enhance: true },
  { src: HISTORICAL[11].src, caption: HISTORICAL[11].caption, category: "Service", enhance: true },
  { src: GALLERY[4].src, caption: "A contemplative moment", category: "Portrait" },
  { src: HISTORICAL[12].src, caption: HISTORICAL[12].caption, category: "Honors", enhance: true },
  { src: HISTORICAL[13].src, caption: HISTORICAL[13].caption, category: "Service", enhance: true },
  { src: HISTORICAL[14].src, caption: HISTORICAL[14].caption, category: "Honors", enhance: true },
  { src: HISTORICAL[15].src, caption: HISTORICAL[15].caption, category: "Author", enhance: true },
  { src: HISTORICAL[16].src, caption: HISTORICAL[16].caption, category: "Service", enhance: true },
  { src: HISTORICAL[18].src, caption: HISTORICAL[18].caption, category: "Service", enhance: true },
  { src: HISTORICAL[20].src, caption: HISTORICAL[20].caption, category: "Sports", enhance: true },
  { src: HISTORICAL[21].src, caption: HISTORICAL[21].caption, category: "Public Life", enhance: true },
  { src: HISTORICAL[24].src, caption: HISTORICAL[24].caption, category: "Honors", enhance: true },
  { src: HISTORICAL[25].src, caption: HISTORICAL[25].caption, category: "Family", enhance: true },
  { src: HISTORICAL[27].src, caption: HISTORICAL[27].caption, category: "Honors", enhance: true },
  { src: HISTORICAL[29].src, caption: HISTORICAL[29].caption, category: "Public Life", enhance: true },
  { src: HISTORICAL[33].src, caption: HISTORICAL[33].caption, category: "Service", enhance: true },
  { src: HISTORICAL[34].src, caption: HISTORICAL[34].caption, category: "Press", enhance: true },
  { src: HISTORICAL[31].src, caption: HISTORICAL[31].caption, category: "Honors", enhance: true },
  { src: HISTORICAL[9].src, caption: HISTORICAL[9].caption, category: "Public Life", enhance: true },
  { src: HISTORICAL[0].src, caption: HISTORICAL[0].caption, category: "Portrait", enhance: true }
];

export default function Gallery() {
  const [active, setActive] = useState(null);
  const [extra, setExtra] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    let mounted = true;
    listPhotos()
      .then((data) => {
        if (!mounted) return;
        setExtra((data || []).map((p) => ({ ...p, category: p.category || "Recent" })));
      })
      .catch(() => { /* silent — admin-uploaded photos are optional */ });
    return () => { mounted = false; };
  }, []);

  const all = [...FEATURED, ...extra];
  const categories = ["All", ...Array.from(new Set(all.map(p => p.category)))];
  const visible = filter === "All" ? all : all.filter(p => p.category === filter);

  const open = (p) => setActive({ ...p, index: visible.indexOf(p) });
  const close = () => setActive(null);
  const nav = (dir) => {
    const next = (active.index + dir + visible.length) % visible.length;
    setActive({ ...visible[next], index: next });
  };

  return (
    <section id="gallery" className="scroll-smooth-section py-24 bg-onyx relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute -top-32 -left-20 w-[500px] h-[500px] rounded-full bg-emerald-deep/30 blur-3xl" />
      <div className="absolute -bottom-32 -right-20 w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl" />
      <div className="absolute inset-x-0 top-0 kerala-border opacity-60" />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-3xl text-ivory">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-champagne" />
              <span className="text-[11px] tracking-[0.35em] uppercase text-champagne font-medium">Photo Gallery</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              A life, <span className="italic text-champagne">in pictures</span>
            </h2>
            <p className="mt-4 text-ivory/70 text-lg max-w-2xl">
              From the consulting room to traditional Kerala mundu — quiet glimpses into a public life.
            </p>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2">
            {categories.map(c => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] border transition-all ${
                  filter === c
                    ? "bg-champagne text-emerald-deep border-champagne"
                    : "border-ivory/20 text-ivory/70 hover:border-champagne hover:text-champagne"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Editorial grid */}
        <div className="mt-14 grid grid-cols-12 gap-4 md:gap-5">
          {visible.map((p, i) => {
            // editorial pattern: first item large; pattern repeats
            const idx = i % 5;
            const cls =
              idx === 0
                ? "col-span-12 md:col-span-7 row-span-2 aspect-[4/5] md:aspect-auto md:h-[640px]"
                : idx === 1
                ? "col-span-6 md:col-span-5 aspect-[4/3] md:h-[310px]"
                : idx === 2
                ? "col-span-6 md:col-span-5 aspect-[4/3] md:h-[310px]"
                : idx === 3
                ? "col-span-6 md:col-span-6 aspect-[4/3] md:h-[280px]"
                : "col-span-6 md:col-span-6 aspect-[4/3] md:h-[280px]";
            return (
              <button
                key={i}
                onClick={() => open(p)}
                className={`relative overflow-hidden rounded-2xl group cursor-zoom-in ring-1 ring-champagne/20 hover:ring-champagne transition-all ${cls}`}
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className={`w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms] ease-out ${p.enhance ? "photo-enhance" : ""}`}
                  style={{ objectPosition: "center 25%" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-onyx/85 via-onyx/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                <div className="absolute top-4 left-4">
                  <span className="bg-onyx/60 backdrop-blur text-champagne text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full border border-champagne/30">
                    {p.category}
                  </span>
                </div>
                <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-onyx/60 backdrop-blur flex items-center justify-center border border-ivory/20 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 className="w-4 h-4 text-ivory" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 text-left">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-champagne">
                    <Camera className="w-3 h-3" /> Photograph #{String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-display text-ivory text-lg md:text-xl mt-1.5 leading-tight">
                    {p.caption}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footnote */}
        <p className="text-center text-ivory/50 text-xs mt-10 italic">
          More photographs added regularly. Upload via the admin panel.
        </p>
      </div>

      {/* Lightbox */}
      {active && (
        <div className="fixed inset-0 z-[60] bg-onyx/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-8" onClick={close}>
          <button onClick={close} className="absolute top-6 right-6 w-11 h-11 rounded-full bg-ivory/10 hover:bg-ivory/20 flex items-center justify-center text-ivory border border-ivory/20" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); nav(-1); }} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-ivory/10 hover:bg-champagne hover:text-emerald-deep flex items-center justify-center text-ivory border border-ivory/20 transition-colors">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); nav(1); }} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-ivory/10 hover:bg-champagne hover:text-emerald-deep flex items-center justify-center text-ivory border border-ivory/20 transition-colors">
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="max-w-5xl w-full" onClick={e => e.stopPropagation()}>
            <div className="premium-frame">
              <div className="inner">
                <img src={active.src} alt={active.caption} className={`w-full max-h-[78vh] object-contain bg-onyx ${active.enhance ? "photo-enhance" : ""}`} />
              </div>
            </div>
            <div className="text-center mt-5">
              <span className="bg-champagne/15 text-champagne text-[10px] uppercase tracking-[0.3em] px-3 py-1 rounded-full border border-champagne/30">{active.category}</span>
              <p className="text-ivory font-display text-xl md:text-2xl mt-3 italic">{active.caption}</p>
              <p className="text-ivory/50 text-xs mt-2">{active.index + 1} of {visible.length}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
